import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarDays, Camera, Images, ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const imgSrc = (path) => (path?.startsWith("/") ? path : `${API}/files/${path}`);

const photoCount = (item) => 1 + (item.extra_images || []).length;

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [photoIdx, setPhotoIdx] = useState(0);

  useEffect(() => {
    fetch(`${API}/gallery`)
      .then((r) => r.json())
      .then(setItems)
      .catch(() => {});
  }, []);

  useEffect(() => {
    setPhotoIdx(0);
  }, [active]);

  if (!items.length) return null;

  const photos = active ? [active.image_path, ...(active.extra_images || [])] : [];

  return (
    <section id="events" data-testid="events-section" className="scroll-mt-24 bg-brand-sand py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <div className="mb-14 max-w-2xl">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-brand-pink" data-testid="events-eyebrow">
            <Camera className="h-4 w-4" /> Fundraisers & Events
          </p>
          <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight sm:text-4xl" data-testid="events-heading">
            Milestones worth celebrating
          </h2>
          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            Community fundraisers, in-store events and the moments we're proud of — in pictures and stories.
          </p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="events-grid">
          {items.map((item, i) => (
            <motion.button
              key={item.item_id}
              data-testid={`event-card-${i}`}
              onClick={() => setActive(item)}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.45, delay: (i % 3) * 0.08 }}
              className="group relative overflow-hidden rounded-2xl text-left shadow-[0_8px_32px_rgba(0,0,0,0.08)] transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-[0_16px_44px_rgba(0,0,0,0.16)]"
            >
              <img
                src={imgSrc(item.image_path)}
                alt={item.title}
                className="h-72 w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
              {item.event_date && (
                <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-bold text-foreground" data-testid={`event-date-${i}`}>
                  {item.event_date}
                </span>
              )}
              {photoCount(item) > 1 && (
                <span className="absolute right-4 top-4 flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1 text-xs font-semibold text-white" data-testid={`event-count-${i}`}>
                  <Images className="h-3 w-3" /> {photoCount(item)} photos
                </span>
              )}
              <div className="absolute inset-x-0 bottom-0 p-5">
                <h3 className="font-heading text-lg font-bold leading-snug text-white">{item.title}</h3>
                {item.story && <p className="mt-1 line-clamp-2 text-sm text-white/75">{item.story}</p>}
              </div>
            </motion.button>
          ))}
        </div>

        <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
          <DialogContent data-testid="event-dialog" className="max-w-2xl overflow-hidden rounded-3xl border-border/60 p-0">
            {active && (
              <div>
                <div className="relative" data-testid="event-photo-viewer">
                  <img
                    src={imgSrc(photos[photoIdx])}
                    alt={`${active.title} — photo ${photoIdx + 1}`}
                    className="max-h-80 w-full object-cover"
                    data-testid="event-dialog-image"
                  />
                  {photos.length > 1 && (
                    <>
                      <button
                        onClick={() => setPhotoIdx((photoIdx - 1 + photos.length) % photos.length)}
                        data-testid="event-photo-prev"
                        aria-label="Previous photo"
                        className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white transition-colors duration-200 hover:bg-black/75"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => setPhotoIdx((photoIdx + 1) % photos.length)}
                        data-testid="event-photo-next"
                        aria-label="Next photo"
                        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white transition-colors duration-200 hover:bg-black/75"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                      <span className="absolute bottom-3 right-3 rounded-full bg-black/60 px-3 py-1 text-xs font-semibold text-white" data-testid="event-photo-counter">
                        {photoIdx + 1} / {photos.length}
                      </span>
                    </>
                  )}
                </div>
                {photos.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto px-7 pt-4" data-testid="event-thumbs">
                    {photos.map((p, i) => (
                      <button
                        key={i}
                        onClick={() => setPhotoIdx(i)}
                        data-testid={`event-thumb-${i}`}
                        aria-label={`View photo ${i + 1}`}
                        className={`shrink-0 overflow-hidden rounded-lg border-2 transition-colors duration-200 ${i === photoIdx ? "border-brand-pink" : "border-transparent opacity-60 hover:opacity-100"}`}
                      >
                        <img src={imgSrc(p)} alt="" className="h-14 w-20 object-cover" />
                      </button>
                    ))}
                  </div>
                )}
                <div className="p-7">
                  {active.event_date && (
                    <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-brand-greendark">
                      <CalendarDays className="h-3.5 w-3.5" /> {active.event_date}
                    </p>
                  )}
                  <h3 className="mt-2 font-heading text-2xl font-bold tracking-tight" data-testid="event-dialog-title">{active.title}</h3>
                  {active.story && (
                    <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-foreground/85" data-testid="event-dialog-story">
                      {active.story}
                    </p>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
}
